import { useState } from 'react';
import type { ReactNode } from 'react';
import Media from 'react-media';
import { flattenDeep, isNull } from 'lodash';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { Chart, Axis, Title, Wrapper } from './styled';
import { Row } from '@simplywallst/ui-core';
import { useTheme } from '@simplywallst/ui-core';
import Bar from '../Bar';

type Value = number | null;

interface ComputedBar {
  title: string;
  value: Value;
  formattedValue: string;
  width: number;
  x?: number;
  y: number;
  color: string;
  titleColor?: string;
  valueColor?: string;
  tooltip?: ReactNode;
  height: number;
}

interface ChartType {
  title: string;
  zero: number;
  bars: ComputedBar[];
}

export interface Props {
  canStack?: boolean;
  charts: string[];
  /**
   * A set of labels to display for each chart.
   * If not provided the keys themselves will be displayed directly.
   *
   * Added as a backward-compatible way to support localised labels.
   */
  chartLabels?: string[];
  colors: string[];
  entities: Record<string, Record<string, number | null>>;
  entitiesFormatted: Record<string, Record<string, string>>;
  entityTooltips?: Record<string, Record<string, ReactNode>>;
  typography?: keyof ReturnType<typeof useTheme>['typography'];
  hideTitle?: boolean;
  height: number;
  keys: string[];
  /**
   * A set of labels to display for each key.
   * If not provided the keys themselves will be displayed directly.
   *
   * Added as a backward-compatible way to support localised labels.
   */
  keyLabels?: string[];
  titleColors?: string[];
  valueColors?: string[];
}

const defaultProps = {
  canStack: true,
  titleColors: [],
  tooltips: [],
};

const BarChartVertical = ({
  canStack,
  charts,
  chartLabels = [],
  colors,
  entities,
  entitiesFormatted,
  entityTooltips,
  typography,
  height,
  hideTitle,
  keys,
  keyLabels = [],
  titleColors,
  valueColors,
}: Props) => {
  const width = 100;
  const theme = useTheme();
  const [stack, setStack] = useState(false);
  const [offset, setOffset] = useState(0);

  const values: number[] = flattenDeep(
    charts.map((chart) => keys.map((key) => entities[chart][key]))
  ).filter((n) => n !== null) as number[];

  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);

  const xScale = scaleBand({
    domain: keys,
    range: [0, width],
  });

  const yScale = scaleLinear({
    domain: [min, max],
    range: [height, offset],
  });

  const color = scaleOrdinal({
    domain: keys,
    range: colors,
  });

  const titleColor = scaleOrdinal({
    domain: keys,
    range: titleColors,
  });

  const valueColor = scaleOrdinal({
    domain: keys,
    range: valueColors,
  });

  const computedCharts: ChartType[] = charts.map((chart, i): ChartType => {
    //  If canStack doesn't === false
    if (canStack && stack) {
      const values: number[] = flattenDeep(
        keys.map((key) => entities[chart][key])
      ).filter((n) => n !== null) as number[];
      const max = Math.max(0, ...values);
      const min = Math.min(0, ...values);
      //  Reset domain min/max
      yScale.domain([min, max]);
    }
    //  Get zero after yScale.domain has been potentially altered due to stacking
    const zero = yScale(0) as number;
    //
    return {
      zero,
      title: chartLabels[i] ?? chart,
      bars: keys.map((key, i): ComputedBar => {
        const value = entities[chart][key];
        const formattedValue = entitiesFormatted[chart][key];
        const height = isNull(value) ? 0 : (yScale(value) as number) - zero;
        return {
          title: keyLabels[i] ?? key,
          value,
          formattedValue,
          width: width / keys.length,
          x: xScale(key),
          y: height < 0 ? height + zero : zero,
          color: color(key),
          tooltip: entityTooltips?.[chart]?.[key],
          titleColor:
            titleColors && titleColors.length > 0 ? titleColor(key) : undefined,
          valueColor:
            valueColors && valueColors.length > 0 ? valueColor(key) : undefined,
          height: Math.abs(height),
        };
      }),
    };
  });

  return (
    <Row>
      {canStack && (
        <Media
          query={`(min-width: ${theme.mdMin})`}
          onChange={(matches) => (matches ? setStack(false) : setStack(true))}
        />
      )}
      {computedCharts.map((chart, i) => {
        const offset = RUNTIME_ENV === 'server' ? 26 : 0;
        return (
          <Wrapper
            key={`bar-group-bar-${i}-${chart.title}`}
            col={canStack ? 12 : 12 / charts.length}
            colMd={12 / charts.length}
            canStack={canStack}
          >
            <div>
              <Axis>
                <svg width="100%" height={height} shapeRendering="crispEdges">
                  <rect
                    x="0"
                    y={chart.zero - 1 + offset}
                    width="100%"
                    height="1"
                    fill={theme.color.white}
                  />
                </svg>
              </Axis>
              <Chart>
                <svg
                  width="100%"
                  role="document"
                  aria-label={chart.title}
                  height={height + offset}
                  shapeRendering="crispEdges"
                  data-cy-id={`bar-chart-${chart.title}`}
                >
                  {chart.bars.map(
                    ({
                      color,
                      height: barHeight,
                      titleColor,
                      valueColor,
                      title,
                      tooltip,
                      value,
                      formattedValue,
                      width: barWidth,
                      x,
                      y,
                    }) => {
                      return (
                        <Bar
                          key={`bar-${i}-${chart.title}-${title}`}
                          x={`${((x || 0) / width) * 100}%`}
                          y={y}
                          tooltip={tooltip}
                          barWidth={`${barWidth}%`}
                          barHeight={barHeight}
                          color={color}
                          title={title}
                          titleColor={titleColor}
                          valueColor={valueColor}
                          value={value}
                          formattedValue={formattedValue}
                          zero={chart.zero}
                          typography={typography}
                          chartHeight={height}
                          onUpdate={(labelHeight) => {
                            const area = height + (chart.zero - height);
                            let offset = 0;
                            if (y === chart.zero) {
                              offset = area - labelHeight;
                            } else {
                              offset = area - labelHeight - barHeight;
                            }
                            if (offset < 0) setOffset(Math.abs(labelHeight));
                          }}
                        />
                      );
                    }
                  )}
                </svg>
              </Chart>
              {!hideTitle && <Title>{chart.title}</Title>}
            </div>
          </Wrapper>
        );
      })}
    </Row>
  );
};

BarChartVertical.defaultProps = defaultProps;

export default BarChartVertical;
